import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { 
  Box, 
  Card, 
  Button, 
  Typography,
  Stack,
  Link as MuiLink,
  Alert,
  CircularProgress,
  Paper
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from "react-query";

import GeneralInfo from "../../../screens/ClientsScreen/GeneralInfo";
import ContactDetails from "../../../screens/ClientsScreen/ContactDetails";
import ReferenceInformation from "../../../screens/ClientsScreen/ReferenceInformation";
import { fetchClient, patchClient } from "../../../api/services/Client";
import { QK } from "../../../base/qk";
import PersonalDetails from "../../../screens/ClientsScreen/PersonalDetails";

const defaultAddress = { 
  id: 1, 
  address_1: "", 
  address_2: "", 
  town: "", 
  county: "", 
  postcode: "",
  lat: null,
  lng: null,
  address_type: "permanent",
  is_service_delivery: true 
};

const defaultPhone = { 
  id: 1, 
  number: "", 
  phone_type: "mobile", 
  preference: 1 
};

const defaultEmail = { 
  id: 1, 
  email: "", 
  email_type: "personal", 
  preference: 1 
};

const ClientUpdate = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();

  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const { data: clientData, isLoading: isFetching, isError } = useQuery(
    [QK.client, id],
    () => fetchClient(id),
    { 
      enabled: !!id,
      retry: false,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  useEffect(() => {
    if (clientData && !isDataLoaded) {
      console.log("Client data received:", clientData);
      setFormData({
        first_name: clientData.first_name || "",
        middle_name: clientData.middle_name || "",
        last_name: clientData.last_name || "",
        title: clientData.title || "",
        suffix_name: clientData.suffix_name || "",
        maiden_name: clientData.maiden_name || "",
        preferred_name: clientData.preferred_name || "",
        birthdate: clientData.birthdate || "",
        marital_status: clientData.marital_status || "",
        gender: clientData.gender || "",
        religion: clientData.religion || "",
        ethnicity: clientData.ethnicity || "",
        nhs_number: clientData.nhs_number || "",
        status: clientData.status || "Active",
        location: clientData.location || "",
        area: clientData.area || "",
        profile_photo: clientData.profile_photo || "",
        preferred_contact_method: clientData.preferred_contact_method || "",
        addresses: clientData.addresses && clientData.addresses.length > 0 ? clientData.addresses : [defaultAddress],
        phones: clientData.phones && clientData.phones.length > 0 ? clientData.phones : [defaultPhone],
        emails: clientData.emails && clientData.emails.length > 0 ? clientData.emails : [defaultEmail],
        ref_service_user_id: clientData.ref_service_user_id || "",
        ref_location: clientData.ref_location || "",
        ref_start_date: clientData.ref_start_date || "",
        ref_end_date: clientData.ref_end_date || "",
        ref_date_of_death: clientData.ref_date_of_death || "",
        ref_key_worker: clientData.ref_key_worker || "",
        ref_named_nurse: clientData.ref_named_nurse || "",
        ni_number: clientData.ni_number || "",
        area_preference: clientData.area_preference || "",
        order_number: clientData.order_number || "",
        as_number: clientData.as_number || "",
        las_number: clientData.las_number || "",
        case_number: clientData.case_number || "",
        sproc_number: clientData.sproc_number || "",
        ref_funding_route: clientData.ref_funding_route || "",
        ref_category_of_care: clientData.ref_category_of_care || "",
      });
      setIsDataLoaded(true);
    }
  }, [clientData, isDataLoaded]);

  const handleChange = (event) => {
    const { name, value } = event.target || {};
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const setFormDataValue = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const updateMutation = useMutation(
    ({ id, data }) => patchClient(id, data),
    {
      onSuccess: () => {
        enqueueSnackbar("Client updated successfully.", { variant: "success" });
        queryClient.invalidateQueries([QK.client, id]);
        queryClient.invalidateQueries(QK.clients);
      },
      onError: (error) => {
        handleError(error);
      }
    }
  );

  const handleError = (error) => {
    console.error("Error:", error);
    const errorObj = {};
    
    if (error.response?.data) {
      const data = error.response.data;
      if (typeof data === "object") {
        Object.entries(data).forEach(([field, messages]) => {
          const msg = Array.isArray(messages) ? messages[0] : messages;
          errorObj[field] = msg;
          enqueueSnackbar(`${field}: ${msg}`, { variant: "error" });
        });
        setErrors(errorObj);
      } else if (typeof data === "string") {
        enqueueSnackbar(data, { variant: "error" });
      }
    } else if (error.message) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  const buildPayload = () => ({
    first_name: formData?.first_name || "",
    middle_name: formData?.middle_name || "",
    last_name: formData?.last_name || "",
    title: formData?.title || "",
    suffix_name: formData?.suffix_name || "",
    maiden_name: formData?.maiden_name || "",
    preferred_name: formData?.preferred_name || "",
    birthdate: formData?.birthdate || null,
    marital_status: formData?.marital_status || "",
    gender: formData?.gender || "",
    religion: formData?.religion || "",
    ethnicity: formData?.ethnicity || "",
    nhs_number: formData?.nhs_number || "",
    status: formData?.status || "Active",
    location: formData?.location || "",
    area: formData?.area || "",
    profile_photo: formData?.profile_photo || "",
    preferred_contact_method: formData?.preferred_contact_method || "",
    addresses: formData?.addresses || [],
    phones: formData?.phones || [],
    emails: formData?.emails || [],
    ref_service_user_id: formData?.ref_service_user_id || "",
    ref_location: formData?.ref_location || "",
    ref_start_date: formData?.ref_start_date || null,
    ref_end_date: formData?.ref_end_date || null,
    ref_date_of_death: formData?.ref_date_of_death || null,
    ref_key_worker: formData?.ref_key_worker || "",
    ref_named_nurse: formData?.ref_named_nurse || "",
    ni_number: formData?.ni_number || "",
    area_preference: formData?.area_preference || "",
    order_number: formData?.order_number || "",
    as_number: formData?.as_number || "",
    las_number: formData?.las_number || "",
    case_number: formData?.case_number || "",
    sproc_number: formData?.sproc_number || "",
    ref_funding_route: formData?.ref_funding_route || "",
    ref_category_of_care: formData?.ref_category_of_care || "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData) return;
    const payload = buildPayload();
    updateMutation.mutate({ id, data: payload });
  };

  if (isFetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", width: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError && !isDataLoaded) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", width: "100%" }}>
        <Paper sx={{ p: 4, textAlign: "center", maxWidth: 500 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Client Not Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The client you are trying to edit does not exist or has been removed.
          </Typography>
          <Button 
            variant="contained" 
            component={Link} 
            to="/clients"
            sx={{ textTransform: "none" }}
          >
            Back to Clients
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!formData && !isFetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", width: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", py: 2, px: { xs: 2, md: 4 } }}>
      <Box sx={{ mx: "auto", width: "100%" }}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#101828' }}>
                Edit Service User
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Update the information below to modify this Service User.
              </Typography>
            </Box>
            <Button 
              variant="outlined"
              component={Link} 
              to="/clients" 
              sx={{ 
                borderRadius: '8px', 
                textTransform: "none", 
                borderColor: '#D0D5DD',
                color: '#344054',
                '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D0D5DD' }
              }}
            >
              Back to Dashboard
            </Button>
          </Stack>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: "100%" }}>
          <Stack spacing={3}>
            {Object.keys(errors).length > 0 && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>Please fix the following errors:</Typography>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  {Object.entries(errors).map(([field, message]) => (
                    <li key={field}>
                      <strong>{field.replace(/_/g, ' ').toUpperCase()}:</strong> {message}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            <GeneralInfo 
              handleChange={handleChange} 
              data={formData} 
              setFormData={setFormDataValue}
              errors={errors}
            />
            <PersonalDetails 
              handleChange={handleChange} 
              data={formData}
              setFormData={setFormDataValue}
              errors={errors}
            />
            <ContactDetails 
              handleChange={handleChange} 
              data={formData}
              setFormData={setFormDataValue}
              errors={errors}
              isEdit={true}
            />
            <ReferenceInformation 
              handleChange={handleChange} 
              data={formData}
              setFormData={setFormDataValue}
              errors={errors}
            />

            <Card 
              variant="outlined" 
              sx={{ 
                p: 2, 
                borderRadius: '12px', 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: 2,
                boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.02)',
                border: '1px solid #EAECF0'
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate('/clients')}
                startIcon={<CloseIcon />}
                sx={{ 
                  borderRadius: '8px', 
                  px: 3, 
                  textTransform: 'none',
                  color: '#344054',
                  borderColor: '#D0D5DD'
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                startIcon={<SaveIcon />}
                disabled={updateMutation.isLoading}
                sx={{ 
                  borderRadius: '8px', 
                  px: 4, 
                  textTransform: 'none',
                  bgcolor: '#1570EF',
                  '&:hover': { bgcolor: '#1056B3' }
                }}
              >
                {updateMutation.isLoading ? "Updating..." : "Update Client"}
              </Button>
            </Card>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientUpdate;